import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '~/components/data-display/Icon';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import BoxIcon from '~/icons/icon-box.svg?react';
import { useFixedAssetsSpeciesQuery } from '~/services/bookkeeping/fixed-assets-species';
import { formatPercentageLimitingDecimals } from '~/utils/formatNumber';
import { formatMoney } from '~/utils/money';
import { FixedAssetFormValues } from '~/views/bookkeeping/VAT-registration/fixed-asset/fixedAssetFormSchema';
import styles from './BookkeepingInvoicesFixedAssetsPreview.module.scss';

export interface BookkeepingInvoicesPreviewFixedAsset {
  asset: FixedAssetFormValues;
  currency: string;
}

interface Props {
  fixedAssets: BookkeepingInvoicesPreviewFixedAsset[];
}

const PreviewField = ({ label, value }: { label: string; value: string }) => (
  <HStack gap={0.5} align="center">
    <Text variant="caption" weight="semibold">
      {label}:
    </Text>
    <Text variant="caption" weight="medium">
      {value}
    </Text>
  </HStack>
);

const BookkeepingInvoicesFixedAssetsPreview = ({ fixedAssets }: Props) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'registrations.documents.table.preview.fixed_assets' });

  const { data: companySpecies = [] } = useFixedAssetsSpeciesQuery();

  const categoryNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const companySpecie of companySpecies) {
      for (const speciesCategory of companySpecie.species.speciesCategories ?? []) {
        if (speciesCategory.category?.code) map.set(speciesCategory.category.code, speciesCategory.category.name);
      }
    }
    return map;
  }, [companySpecies]);

  const speciesNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const companySpecie of companySpecies) map.set(companySpecie.species.id, companySpecie.species.name);
    return map;
  }, [companySpecies]);

  if (fixedAssets.length === 0) {
    return null;
  }

  return (
    <VStack gap={1} data-testid="invoices-registration-preview-fixed-assets">
      <HStack gap={1} align="center">
        <Icon component={BoxIcon} className={styles.icon} />
        <Text variant="body1" weight="semibold">
          {t('title')}
        </Text>
      </HStack>

      {fixedAssets.map(({ asset, currency }, index) => {
        const name = asset.description || (asset.species ? (speciesNameById.get(asset.species) ?? '') : '') || '-';
        const amount = asset.purchasePrice ? formatMoney({ amount: asset.purchasePrice, currency }) : '-';
        const category = (asset.category ? categoryNameByCode.get(asset.category) : undefined) ?? '-';
        const depreciationRate = asset.bookDepreciationRate
          ? formatPercentageLimitingDecimals(Number(asset.bookDepreciationRate))
          : '-';
        const assetTypeLabel =
          asset.assetType === 'INTANGIBLE'
            ? t('asset_type_intangible')
            : asset.assetType === 'TANGIBLE'
              ? t('asset_type_tangible')
              : '-';

        return (
          <VStack key={index} gap={1} className={styles.card}>
            <HStack gap={1} align="center">
              <Icon component={BoxIcon} className={styles.icon} />
              <Text variant="body2" weight="semibold">
                {name}
              </Text>
            </HStack>

            <HStack gap={2} align="center">
              <PreviewField label={t('amount')} value={amount} />
              <PreviewField label={t('category')} value={category} />
              <PreviewField label={t('depreciation_rate')} value={depreciationRate} />
              <PreviewField label={t('type')} value={assetTypeLabel} />
            </HStack>
          </VStack>
        );
      })}
    </VStack>
  );
};

export default BookkeepingInvoicesFixedAssetsPreview;
